import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import TitlesIcon from "@mui/icons-material/MovieOutlined";
import UploadIcon from "@mui/icons-material/CloudUploadOutlined";
import RevenueIcon from "@mui/icons-material/PaymentsOutlined";
import RentalsIcon from "@mui/icons-material/ShoppingBagOutlined";
import AudienceIcon from "@mui/icons-material/InsightsOutlined";
import CustomersIcon from "@mui/icons-material/StorefrontOutlined";
import BrandIcon from "@mui/icons-material/BadgeOutlined";
import AgreementIcon from "@mui/icons-material/GavelOutlined";
import TeamIcon from "@mui/icons-material/GroupOutlined";
import PlansIcon from "@mui/icons-material/TableChartOutlined";
import InformationIcon from "@mui/icons-material/InfoOutlined";
import ReportsIcon from "@mui/icons-material/AssessmentOutlined";
import ApplicationsIcon from "@mui/icons-material/HowToRegOutlined";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import BackIcon from "@mui/icons-material/ArrowBackOutlined";
import TranslateIcon from "@mui/icons-material/TranslateOutlined";
import { Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  canActForOwners,
  enterOwnerShell,
  leaveOwnerShell,
} from "../auth/actingOwner";
import { useAuth } from "../auth/AuthContext";
import type { Need } from "@shared/access/check";
import { accessOf, allows } from "../auth/access";
import { actingOwner, foyerApi, type OwnerShell } from "../http/foyer";
import { tokens } from "../theme";
import { useFormat } from "../i18n/format";
import { useLanguage } from "../i18n/LanguageProvider";
import { LanguageMenuItems } from "../i18n/LanguageSwitcher";
import { LANGUAGE_NAMES } from "../i18n/languages";

/**
 * The supplier shell — where a content owner lands (`CONTENT-4`).
 *
 * ## Why a shell and not a filtered operator menu
 *
 * Foyer's nav is estate-first: a premise is a place and `License.product`
 * decides which areas exist. A content owner has no estate — no company, no
 * locations, no devices, no fleet — so dropping them into `RootLayout` and
 * hiding fourteen sections would give an external party a menu that is mostly
 * evidence of what they cannot see, and put the boundary one `canSee()`
 * regression away from a leak.
 *
 * It is a SHELL, not a second app: same auth, same build, same components. Only
 * the layout and the nav differ.
 *
 * ## It wears the owner's brand, within a floor
 *
 * The theme is resolved on the server (`GET /owner/shell`) through the same
 * role vocabulary the relatives portal uses. Two things branding cannot reach,
 * both enforced in the token layer rather than by convention: every ink role is
 * contrast-checked against the ground it lands on, and `danger` is Foyer's own
 * regardless of what a partner sets — a studio must not be able to make the
 * button that deletes their material look like a Save.
 *
 * ## Copy
 *
 * The audience is a commercial partner, not an operator. Empty states say what
 * happens next and who to ask, never "no data".
 */

/**
 * The menu, as a CMS-shaped sidebar.
 *
 * ## 🚨 Its own list, not `RootLayout`'s with entries hidden
 *
 * Asked for as *"the same menu as for admins but with just the stuff they
 * need"* — and that is answered on SHAPE, not on mechanism. Reusing the
 * operator nav and filtering it would give an external party a menu that is
 * mostly evidence of what they cannot see, and would put the boundary one
 * `canSee()` regression away from a leak. This array names only surfaces a
 * content owner is entitled to, so there is no filter to regress.
 *
 * Grouped the way the work is: what they SUPPLY, what they EARN, and who they
 * ARE. A flat list of seven reads as a pile.
 */
type ShellGroup = "content" | "business" | "organisation";
type ShellNavKey =
  | "myTitles"
  | "upload"
  | "information"
  | "audience"
  | "plans"
  | "applications"
  | "rentals"
  | "revenue"
  | "reports"
  | "yourCustomers"
  | "agreements"
  | "credits"
  | "team";

const SHELL_NAV: Array<{
  group: ShellGroup;
  items: Array<{
    to: string;
    label: ShellNavKey;
    icon: JSX.Element;
    /** Shown only to a login with this level at the owner. Absent = everyone. */
    access?: Need;
  }>;
}> = [
  {
    group: "content",
    items: [
      { to: "/supplier/titles", label: "myTitles", icon: <TitlesIcon /> },
      { to: "/supplier/upload", label: "upload", icon: <UploadIcon /> },
      // The portal's members-only rail of short clips and PDFs (2026-10-09).
      {
        to: "/supplier/information",
        label: "information",
        icon: <InformationIcon />,
      },
    ],
  },
  {
    group: "business",
    items: [
      // Audience before revenue: a partner asks "did anybody watch it" before
      // "what did it earn", and the second only makes sense after the first.
      { to: "/supplier/audience", label: "audience", icon: <AudienceIcon /> },
      // Rentals on YOUR portal (whoever's title); Revenue is what YOUR
      // titles earned anywhere. Two questions, two pages.
      // What organisations can buy from your portal (ORG-1): the yearly
      // plan matrix and the one-off rental, moved here from Nenda admin.
      { to: "/supplier/plans", label: "plans", icon: <PlansIcon /> },
      // Organisations waiting to be let in (ORG-3).
      {
        to: "/supplier/applications",
        label: "applications",
        icon: <ApplicationsIcon />,
      },
      { to: "/supplier/rentals", label: "rentals", icon: <RentalsIcon /> },
      { to: "/supplier/revenue", label: "revenue", icon: <RevenueIcon /> },
      // ORG-8: picks per studio and plan sales, for the studios' split.
      { to: "/supplier/reports", label: "reports", icon: <ReportsIcon /> },
      {
        to: "/supplier/customers",
        label: "yourCustomers",
        icon: <CustomersIcon />,
      },
      {
        to: "/supplier/agreements",
        label: "agreements",
        icon: <AgreementIcon />,
      },
    ],
  },
  {
    group: "organisation",
    items: [
      { to: "/supplier/brand", label: "credits", icon: <BrandIcon /> },
      // CONTENT-23: only the organisation's team admin manages its people.
      {
        to: "/supplier/team",
        label: "team",
        icon: <TeamIcon />,
        access: { resource: "users", level: "view" },
      },
    ],
  },
];

/*
 * 🚨 There is no "Team" entry, and that is a finding rather than an omission.
 *
 * An owner is an ORGANISATION with several logins (`CONTENT-23`), but nothing
 * owner-facing can invite one: a colleague's login is a `User` row with
 * `type: "contentOwner"`, created by Nenda staff on the Users page. The
 * `/owner/members` routes are NOT that — they are the people at a supplier's
 * own CUSTOMERS (schools, libraries), and "Your customers" already draws them.
 *
 * A nav entry with nothing behind it teaches a partner that the product is a
 * mock-up, so this waits for a route that exists.
 */

const NAV_WIDTH = 248;

/**
 * 🚨 There is no theme here, and that is the point.
 *
 * `CONTENT-4` built this shell as the WHITE-LABEL surface: the server resolved
 * a palette from the owner's branding block and every page wore it. Sigge
 * withdrew that — *"I never asked for my brand - we can drop that entirely"* —
 * so the supplier pages are Nenda Portal pages, drawn from `theme.ts` like
 * every CMS screen, and the outlet context carries no palette to disagree with
 * it.
 *
 * Deleted rather than defaulted. A theme still being resolved, threaded and
 * ignored is a mechanism waiting to be switched back on by accident, and it is
 * exactly how "why is this page purple" starts.
 */

export default function SupplierShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(["supplier", "common", "shell"]);
  const fmt = useFormat();
  const { language, languages } = useLanguage();
  const [languageAnchor, setLanguageAnchor] = useState<HTMLElement | null>(
    null
  );
  /*
   * A Nenda admin supporting a supplier (2026-10-14): same shell, same pages,
   * plus a switcher between owners and a way back to Nenda instead of
   * "Sign out". Every page is keyed by the owner below, so switching reloads
   * them rather than showing one owner's rows under another's name.
   */
  const adminView = canActForOwners(user);
  const access = accessOf(user);
  const [ownerId, setOwnerId] = useState<string | null>(actingOwner());
  const [owners, setOwners] = useState<Array<{ id: string; name: string }>>([]);
  const [shell, setShell] = useState<OwnerShell | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setShell(null);
    setError(null);
    foyerApi
      .ownerShell()
      .then((s) => live && setShell(s))
      .catch(
        (e) => live && setError(e instanceof Error ? e.message : String(e))
      );
    return () => {
      live = false;
    };
  }, [ownerId]);

  useEffect(() => {
    if (!adminView) return;
    let live = true;
    foyerApi.listContentOwners().then(
      (rows) =>
        live &&
        setOwners(
          rows
            .map((o) => ({ id: o.id, name: o.name }))
            .sort((a, b) => a.name.localeCompare(b.name, fmt.locale))
        ),
      () => live && setOwners([])
    );
    return () => {
      live = false;
    };
  }, [adminView, fmt.locale]);

  const mayOpen = (need: Need) =>
    allows(access, need, { companyId: ownerId ?? "" });

  const switchOwner = (id: string) => {
    enterOwnerShell(id);
    setOwnerId(id);
  };

  const backToNenda = () => {
    leaveOwnerShell();
    navigate("/distribution/providers");
  };

  if (error && adminView) {
    return (
      <Box sx={{ p: 4, maxWidth: 560, mx: "auto" }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {t("supplierShell.shellOpenFailed", { error })}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={backToNenda}>
          {t("supplierShell.backToNenda")}
        </Button>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, maxWidth: 560, mx: "auto" }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {t("supplierShell.accountOpenFailed")}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          {t("supplierShell.notSetUp", { email: user?.user ?? "" })}
        </Typography>
        <Button sx={{ mt: 2 }} onClick={logout}>
          {t("shell:account.signOut")}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      data-scheme="nenda-portal"
      sx={{
        display: "flex",
        minHeight: "100vh",
        background: tokens.canvas,
        color: tokens.ink,
        fontFamily: undefined,
      }}
    >
      <Drawer
        variant="permanent"
        sx={{
          width: NAV_WIDTH,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: NAV_WIDTH,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            /* The owner's own surface, not Foyer's paper. The sidebar is the
               most branded thing on screen, which is the point of a shell that
               wears their identity at all. */
            background: tokens.surface,
            color: tokens.ink,
            borderRight: `1px solid ${tokens.divider}`,
          },
        }}
        data-testid="supplier-nav"
      >
        <Box
          sx={{
            px: 2.5,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            minHeight: 68,
          }}
        >
          {/* The owner's NAME, in the CMS's own voice — not their wordmark.
              An operator and a supplier are looking at one product, and the
              eyebrow is what says which account this is rather than a second
              brand competing with Nenda's. */}
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography
              variant="overline"
              sx={{
                color: tokens.inkMuted,
                typography: "micro",
                letterSpacing: 1,
              }}
            >
              {t("supplierShell.contentOwner")}
            </Typography>
            {adminView ? (
              <TextField
                select
                size="small"
                value={ownerId ?? ""}
                onChange={(e) => switchOwner(e.target.value)}
                inputProps={{ "aria-label": t("supplierShell.contentOwner") }}
                data-testid="supplier-owner-switch"
                sx={{ minWidth: 0 }}
              >
                {owners.length === 0 && ownerId ? (
                  <MenuItem value={ownerId}>
                    {shell?.owner.name ?? ownerId}
                  </MenuItem>
                ) : null}
                {owners.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Typography
                sx={{ fontWeight: "fontWeightBold", typography: "titleSm" }}
                noWrap
              >
                {shell?.owner.name ?? " "}
              </Typography>
            )}
            {adminView ? (
              <Typography
                variant="caption"
                sx={{ color: tokens.inkMuted }}
                data-testid="supplier-admin-view"
              >
                {t("supplierShell.adminViewNote")}
              </Typography>
            ) : null}
          </Stack>
        </Box>

        <Divider sx={{ borderColor: tokens.divider }} />

        <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
          {SHELL_NAV.map((section) => (
            <Box key={section.group} sx={{ mb: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  px: 2.5,
                  color: tokens.inkFaint,
                  typography: "micro",
                  letterSpacing: 1,
                }}
              >
                {t(`supplierShell.groups.${section.group}`)}
              </Typography>
              <List dense disablePadding>
                {section.items
                  .filter((item) => !item.access || mayOpen(item.access))
                  .map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      style={{ textDecoration: "none" }}
                    >
                      {({ isActive }) => (
                        <ListItemButton
                          selected={isActive}
                          data-testid={`supplier-nav-${item.to.split("/").pop()}`}
                          /* 🚨 No colours. The admin sidebar sets none either
                           — `RootLayout.renderNavItem` passes `selected` and
                           lets the theme's `MuiListItemButton` override paint
                           it. Restating the active treatment here is how the
                           two drifted apart in the first place: this one had
                           a tint where the admin nav has a solid fill, and
                           the difference was visible at a glance. */
                          sx={{ mx: 1, my: 0.25, borderRadius: 2, px: 1.5 }}
                        >
                          <ListItemIcon
                            sx={{ minWidth: 40, justifyContent: "center" }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={t(`supplierShell.nav.${item.label}`)}
                          />
                        </ListItemButton>
                      )}
                    </NavLink>
                  ))}
              </List>
            </Box>
          ))}
        </Box>

        <Divider sx={{ borderColor: tokens.divider }} />

        <Stack sx={{ p: 2 }} spacing={0.5}>
          <Typography
            variant="caption"
            sx={{ color: tokens.inkFaint }}
            noWrap
            title={user?.user}
          >
            {user?.user}
          </Typography>
          {languages.length > 1 ? (
            <>
              <Button
                size="small"
                startIcon={<TranslateIcon />}
                onClick={(e) => setLanguageAnchor(e.currentTarget)}
                aria-haspopup="menu"
                aria-label={t("common:language.switchTo", {
                  name: LANGUAGE_NAMES[language],
                })}
                sx={{ color: tokens.inkMuted, justifyContent: "flex-start" }}
                data-testid="supplier-language"
              >
                {LANGUAGE_NAMES[language]}
              </Button>
              <Menu
                anchorEl={languageAnchor}
                open={Boolean(languageAnchor)}
                onClose={() => setLanguageAnchor(null)}
                onClick={() => setLanguageAnchor(null)}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                transformOrigin={{ vertical: "bottom", horizontal: "left" }}
              >
                <LanguageMenuItems />
              </Menu>
            </>
          ) : null}
          {adminView ? (
            <Button
              size="small"
              startIcon={<BackIcon />}
              onClick={backToNenda}
              sx={{ color: tokens.inkMuted, justifyContent: "flex-start" }}
              data-testid="supplier-back-to-nenda"
            >
              {t("supplierShell.backToNenda")}
            </Button>
          ) : (
            <Button
              size="small"
              startIcon={<LogoutIcon />}
              onClick={logout}
              sx={{ color: tokens.inkMuted, justifyContent: "flex-start" }}
            >
              {t("shell:account.signOut")}
            </Button>
          )}
        </Stack>
      </Drawer>

      <Box
        component="main"
        sx={{ flex: 1, px: { xs: 2, md: 4 }, py: 4, minWidth: 0 }}
      >
        <Box sx={{ maxWidth: 1100, mx: "auto" }}>
          {shell && !shell.owner.enabled ? (
            <Alert
              severity="warning"
              sx={{ mb: 3 }}
              data-testid="supplier-owner-disabled"
            >
              {t("supplierShell.ownerDisabled")}
            </Alert>
          ) : null}
          {shell ? (
            <Suspense fallback={null}>
              <Outlet key={ownerId ?? ""} context={{ shell }} />
            </Suspense>
          ) : (
            <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
              <CircularProgress size={28} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
